/**
 * OPERATIONAL LINEAGE HASH
 *
 * Deterministic hash computation for lineage graphs.
 * Self-contained — no Math.random, no backend dependency.
 */

import { deterministicStringify } from '../../server/export/operationalBundleHash.js';
import type { OperationalLineageGraph, LineageNode, LineageEdge } from './operationalLineageTypes.js';

/**
 * Compute a deterministic node ID from type and content hash.
 */
export function computeLineageNodeId(type: string, contentHash: string): string {
  return `node:${type}:${contentHash}`;
}

/**
 * Compute a deterministic edge ID from source, target, and type.
 */
export function computeLineageEdgeId(source: string, target: string, edgeType: string): string {
  return `edge:${source}:${target}:${edgeType}`;
}

/**
 * Compute the canonical hash of a lineage graph.
 * Deterministic: same graph → same hash every time.
 */
export async function computeLineageGraphHash(graph: Omit<OperationalLineageGraph, 'graphHash'>): Promise<string> {
  const canonical = deterministicStringify({
    graphVersion: graph.graphVersion,
    bundleHash: graph.bundleHash,
    nodes: graph.nodes.map((n) => ({
      id: n.id,
      type: n.type,
      hash: n.hash,
      metadata: n.metadata,
    })),
    edges: graph.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: e.type,
      metadata: e.metadata,
    })),
  });

  // Simple hex hash using Web Crypto API or node:crypto
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoder = new TextEncoder();
    const buffer = await crypto.subtle.digest('SHA-256', encoder.encode(canonical));
    const hex = Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return `sha256:${hex}`;
  }

  const { createHash } = await import('crypto');
  const hex = createHash('sha256').update(canonical, 'utf8').digest('hex');
  return `sha256:${hex}`;
}

/**
 * Compute a hash for a lineage node payload.
 */
export async function computeNodeHash(payload: Record<string, unknown>): Promise<string> {
  const canonical = deterministicStringify(payload);
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoder = new TextEncoder();
    const buffer = await crypto.subtle.digest('SHA-256', encoder.encode(canonical));
    const hex = Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return `sha256:${hex}`;
  }

  const { createHash } = await import('crypto');
  const hex = createHash('sha256').update(canonical, 'utf8').digest('hex');
  return `sha256:${hex}`;
}
